import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_SCHEDULER_URL || 'http://localhost:5051/api';

const STATUS_META = {
  waiting:  { label: 'Waiting',  bg: '#1e293b', color: '#94a3b8', dot: '#64748b' },
  running:  { label: 'Running',  bg: '#1e3a5f', color: '#60a5fa', dot: '#3b82f6' },
  success:  { label: 'Success',  bg: '#14532d', color: '#4ade80', dot: '#22c55e' },
  failed:   { label: 'Failed',   bg: '#450a0a', color: '#f87171', dot: '#ef4444' },
  blocked:  { label: 'Blocked',  bg: '#431407', color: '#fb923c', dot: '#f97316' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const duration = (start, end) => {
  if (!start || !end) return null;
  const s = Math.round((new Date(end) - new Date(start)) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rem = s % 60;
  return `${m}m ${rem}s`;
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
function Badge({ status }) {
  const m = STATUS_META[status] || STATUS_META.waiting;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      background: m.bg, color: m.color,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0,
        animation: status === 'running' ? 'pulse 1.2s ease-in-out infinite' : 'none'
      }} />
      {m.label}
    </span>
  );
}

// ─── Attempt Log Row ──────────────────────────────────────────────────────────
function AttemptRow({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #1e293b' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', cursor: 'pointer', background: open ? '#0f172a' : 'transparent' }}
      >
        <Badge status={a.status} />
        <span style={{ fontSize: 12, color: '#64748b' }}>Attempt #{a.attempt_number}</span>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{fmt(a.start_time)}</span>
        {a.end_time && <span style={{ fontSize: 12, color: '#64748b' }}>{duration(a.start_time, a.end_time)}</span>}
        <span style={{ fontSize: 11, color: '#475569', marginLeft: 'auto' }}>{a.triggered_by}</span>
        <span style={{ fontSize: 12, color: '#475569' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '8px 12px 12px', background: '#020817' }}>
          {a.stdout && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: '#4ade80', fontWeight: 700, marginBottom: 4, letterSpacing: '0.1em' }}>STDOUT</div>
              <pre style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{a.stdout}</pre>
            </div>
          )}
          {a.stderr && (
            <div>
              <div style={{ fontSize: 10, color: '#f87171', fontWeight: 700, marginBottom: 4, letterSpacing: '0.1em' }}>STDERR</div>
              <pre style={{ fontSize: 11, color: '#fca5a5', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{a.stderr}</pre>
            </div>
          )}
          {!a.stdout && !a.stderr && <span style={{ fontSize: 12, color: '#475569' }}>(no output)</span>}
        </div>
      )}
    </div>
  );
}

// ─── Process Detail Drawer ────────────────────────────────────────────────────
function ProcessDrawer({ process, runDate, onClose }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!process) return;
    setLoading(true);
    fetch(`${API}/runs/${process.id}/attempts?date=${runDate}`)
      .then(r => r.json())
      .then(data => { setAttempts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [process, runDate]);

  if (!process) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div style={{
        width: 560, background: '#0f172a', borderLeft: '1px solid #1e293b',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{process.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{process.id}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e293b', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div><div style={labelStyle}>Status</div><Badge status={process.status || 'waiting'} /></div>
          <div><div style={labelStyle}>Schedule</div><span style={valStyle}>{process.schedule_time || '—'}</span></div>
          <div><div style={labelStyle}>Max retries</div><span style={valStyle}>{process.max_retries}</span></div>
          <div><div style={labelStyle}>Started</div><span style={valStyle}>{fmt(process.start_time)}</span></div>
          <div><div style={labelStyle}>Ended</div><span style={valStyle}>{fmt(process.end_time)}</span></div>
          {process.end_time && <div><div style={labelStyle}>Duration</div><span style={valStyle}>{duration(process.start_time, process.end_time)}</span></div>}
          {process.blocked_by?.length > 0 && (
            <div><div style={labelStyle}>Blocked by</div><span style={{ ...valStyle, color: '#fb923c' }}>{process.blocked_by.join(', ')}</span></div>
          )}
        </div>
        {(process.dependencies || []).length > 0 && (
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #1e293b' }}>
            <div style={labelStyle}>Dependencies</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {process.dependencies.map(d => (
                <span key={d} style={{ fontSize: 11, padding: '2px 8px', background: '#1e293b', color: '#94a3b8', borderRadius: 3, fontFamily: 'monospace' }}>{d}</span>
              ))}
            </div>
          </div>
        )}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e293b' }}>
          <div style={labelStyle}>Script</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>{process.script_path}</div>
        </div>
        {process.script_type === 'python' && (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e293b' }}>
            <div style={labelStyle}>Python venv</div>
            <div style={{ fontSize: 11, color: process.venv_path ? '#94a3b8' : '#475569', fontFamily: 'monospace', marginTop: 2 }}>
              {process.venv_path || '(global default)'}
            </div>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ padding: '12px 20px 8px', fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Run Attempts — {runDate}
          </div>
          {loading ? (
            <div style={{ padding: '16px 20px', color: '#475569', fontSize: 13 }}>Loading…</div>
          ) : attempts.length === 0 ? (
            <div style={{ padding: '16px 20px', color: '#475569', fontSize: 13 }}>No attempts recorded yet.</div>
          ) : (
            attempts.map(a => <AttemptRow key={a.id} a={a} />)
          )}
        </div>
      </div>
    </div>
  );
}

// ─── History View ─────────────────────────────────────────────────────────────
function HistoryView({ processes }) {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 13);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selProcess, setSelProcess] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate, limit: 500 });
    if (selProcess) params.set('process_id', selProcess);
    fetch(`${API}/runs/history?${params}`)
      .then(r => r.json())
      .then(data => { setRows(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [startDate, endDate, selProcess]);

  useEffect(() => { load(); }, [load]);

  // Build date × process grid
  const dates = [];
  const cur = new Date(startDate);
  const end = new Date(endDate);
  while (cur <= end) { dates.push(cur.toISOString().slice(0, 10)); cur.setDate(cur.getDate() + 1); }

  const statusMap = {};
  rows.forEach(r => { statusMap[`${r.process_id}__${r.run_date}`] = r.status; });

  const shown = selProcess ? processes.filter(p => p.id === selProcess) : processes;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={selProcess} onChange={e => setSelProcess(e.target.value)} style={inputStyle}>
          <option value="">All processes</option>
          {processes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
        <span style={{ color: '#475569', fontSize: 13 }}>to</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} />
        <button onClick={load} style={btnStyle}>Reload</button>
      </div>

      {loading ? <div style={{ color: '#475569' }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 600, fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: 160 }}>Process</th>
                {dates.map(d => (
                  <th key={d} style={{ ...thStyle, minWidth: 38, writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 80, paddingBottom: 6 }}>
                    {d.slice(5)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map(p => (
                <tr key={p.id}>
                  <td style={{ padding: '6px 12px', color: '#94a3b8', borderBottom: '1px solid #1e293b', whiteSpace: 'nowrap' }}>{p.name}</td>
                  {dates.map(d => {
                    const s = statusMap[`${p.id}__${d}`] || null;
                    const m = s ? STATUS_META[s] : null;
                    return (
                      <td key={d} style={{ padding: '4px 3px', borderBottom: '1px solid #1e293b', textAlign: 'center' }}>
                        {m && (
                          <div title={`${p.name} · ${d} · ${s}`} style={{
                            width: 28, height: 20, borderRadius: 3,
                            background: m.bg, border: `1px solid ${m.dot}33`,
                            margin: '0 auto'
                          }} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_META).map(([s, m]) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 14, height: 14, borderRadius: 2, background: m.bg, border: `1px solid ${m.dot}55` }} />
                <span style={{ fontSize: 11, color: '#64748b', textTransform: 'capitalize' }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const labelStyle = { fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 };
const valStyle   = { fontSize: 13, color: '#94a3b8' };
const inputStyle = { background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 4, padding: '5px 10px', fontSize: 12 };
const btnStyle   = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const thStyle    = { padding: '6px 8px', background: '#0f172a', color: '#475569', fontWeight: 600, borderBottom: '1px solid #1e293b', textTransform: 'uppercase', letterSpacing: '0.07em' };

export default function SchedulerPage() {
  const [tab, setTab]             = useState('today');
  const [runs, setRuns]           = useState([]);
  const [processes, setProcesses] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [triggering, setTriggering] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError]         = useState(null);
  const [resetHold, setResetHold] = useState(false);
  const [holdToggling, setHoldToggling] = useState(false);
  const [resetting, setResetting] = useState(false);

  const todayNY = new Date().toLocaleDateString('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit'
  });

  const loadToday = useCallback(() => {
    fetch(`${API}/runs/today`)
      .then(r => r.json())
      .then(data => {
        setRuns(data);
        setLastRefresh(new Date());
        setError(null);
      })
      .catch(() => setError('Cannot reach scheduler service.'));
  }, []);

  const loadProcesses = useCallback(() => {
    fetch(`${API}/processes`)
      .then(r => r.json())
      .then(setProcesses)
      .catch(() => {});
  }, []);

  const loadConfig = useCallback(() => {
    fetch(`${API}/config`)
      .then(r => r.json())
      .then(cfg => setResetHold(cfg.reset_hold === 'true'))
      .catch(() => {});
  }, []);

  const handleForceReset = async () => {
    if (!window.confirm('Force a daily reset now? This will fail any running/waiting jobs and re-seed today\'s cycle.')) return;
    setResetting(true);
    try {
      await fetch(`${API}/reset`, { method: 'POST' });
      setTimeout(loadToday, 1000);
    } finally {
      setResetting(false);
    }
  };

  const toggleHold = async () => {
    setHoldToggling(true);
    try {
      const newVal = !resetHold;
      const res = await fetch(`${API}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_hold: String(newVal) }),
      });
      if (res.ok) {
        setResetHold(newVal);
        if (!newVal) setTimeout(loadToday, 1000);
      }
    } finally {
      setHoldToggling(false);
    }
  };

  useEffect(() => {
    loadToday();
    loadProcesses();
    loadConfig();
  }, [loadToday, loadProcesses, loadConfig]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(loadToday, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, loadToday]);

  const handleTrigger = async (processId, e) => {
    e.stopPropagation();
    setTriggering(t => ({ ...t, [processId]: true }));
    try {
      const res = await fetch(`${API}/trigger/${processId}`, { method: 'POST' });
      if (res.ok) { setTimeout(loadToday, 800); }
    } finally {
      setTriggering(t => ({ ...t, [processId]: false }));
    }
  };

  // Summary counts
  const counts = Object.fromEntries(Object.keys(STATUS_META).map(s => [s, 0]));
  runs.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });

  const todayDate = new Date().toISOString().slice(0, 10);
  const selectedWithMeta = selected
    ? { ...selected, ...runs.find(r => r.id === selected.id) }
    : null;

  return (
    <div style={{ minHeight: '100vh', background: '#020817', color: '#e2e8f0', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        * { box-sizing: border-box; }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: '1px solid #1e293b', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>Process Scheduler</div>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{todayNY} · New York time</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastRefresh && (
            <span style={{ fontSize: 11, color: '#334155' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button
            onClick={toggleHold}
            disabled={holdToggling}
            style={{
              ...btnStyle,
              background: resetHold ? '#ea580c' : '#1e293b',
              border: resetHold ? 'none' : '1px solid #334155',
              opacity: holdToggling ? 0.6 : 1,
            }}
          >
            {holdToggling ? '…' : resetHold ? '⏸ Hold ON' : '⏸ Hold'}
          </button>
          <button
            onClick={handleForceReset}
            disabled={resetting}
            title="Force daily reset immediately"
            style={{
              ...btnStyle,
              background: '#4a1d96',
              border: '1px solid #6d28d9',
              opacity: resetting ? 0.6 : 1,
            }}
          >
            {resetting ? '…' : '⟳ Force Reset'}
          </button>
          <button onClick={loadToday} style={btnStyle}>↺ Refresh</button>
        </div>
      </div>

      {resetHold && (
        <div style={{ margin: '12px 28px', padding: '10px 16px', background: '#431407', border: '1px solid #9a3412', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>⏸</span>
            <div>
              <span style={{ color: '#fb923c', fontWeight: 700, fontSize: 13 }}>Reset on Hold</span>
              <span style={{ color: '#9a3412', fontSize: 12, marginLeft: 10 }}>The scheduled reset will not fire until hold is released.</span>
            </div>
          </div>
          <button
            onClick={toggleHold}
            disabled={holdToggling}
            style={{ background: '#ea580c', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: holdToggling ? 0.6 : 1 }}
          >
            {holdToggling ? '…' : 'Release Hold'}
          </button>
        </div>
      )}

      {error && (
        <div style={{ margin: '12px 28px', padding: '10px 14px', background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 4, color: '#fca5a5', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 0, padding: '12px 28px', borderBottom: '1px solid #1e293b' }}>
        {Object.entries(STATUS_META).map(([s, m]) => (
          <div key={s} style={{ marginRight: 24, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.dot }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: m.color }}>{counts[s]}</span>
            <span style={{ fontSize: 12, color: '#475569', textTransform: 'capitalize' }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', padding: '0 28px' }}>
        {['today', 'history'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 16px', fontSize: 13, fontWeight: 600,
              color: tab === t ? '#3b82f6' : '#475569',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
              textTransform: 'capitalize', marginBottom: -1,
            }}
          >{t === 'today' ? "Today's Runs" : 'History'}</button>
        ))}
      </div>

      <div style={{ padding: '20px 28px' }}>

        {/* TODAY tab */}
        {tab === 'today' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Process', 'Status', 'Schedule', 'Dependencies', 'Started', 'Ended', 'Duration', 'Attempts', ''].map(h => (
                  <th key={h} style={{ ...thStyle, textAlign: h === '' ? 'center' : 'left', paddingBottom: 8, paddingTop: 0 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0f172a'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '10px 12px', color: '#f1f5f9', fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '10px 12px' }}><Badge status={r.status || 'waiting'} /></td>
                  <td style={{ padding: '10px 12px', color: '#64748b', fontFamily: 'monospace' }}>{r.schedule_time || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {(r.dependencies || []).length > 0 ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.dependencies.map(d => (
                          <span key={d} style={{ fontSize: 10, padding: '1px 6px', background: '#1e293b', color: '#64748b', borderRadius: 3, fontFamily: 'monospace' }}>{d}</span>
                        ))}
                      </div>
                    ) : <span style={{ color: '#334155' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#64748b', fontFamily: 'monospace' }}>{fmt(r.start_time)}</td>
                  <td style={{ padding: '10px 12px', color: '#64748b', fontFamily: 'monospace' }}>{fmt(r.end_time)}</td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{duration(r.start_time, r.end_time) || '—'}</td>
                  <td style={{ padding: '10px 12px', color: r.attempts > 1 ? '#fb923c' : '#64748b', textAlign: 'center' }}>{r.attempts ?? 0}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <button
                      onClick={e => handleTrigger(r.id, e)}
                      disabled={triggering[r.id] || r.status === 'running'}
                      title="Force trigger"
                      style={{
                        background: '#1e3a5f', border: '1px solid #1e40af', color: '#60a5fa',
                        borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer',
                        fontWeight: 600, opacity: (triggering[r.id] || r.status === 'running') ? 0.4 : 1
                      }}
                    >
                      {triggering[r.id] ? '…' : '▶ Run'}
                    </button>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && !error && (
                <tr>
                  <td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: '#334155' }}>
                    No processes scheduled. Add processes via the API.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* HISTORY tab */}
        {tab === 'history' && <HistoryView processes={processes} />}
      </div>

      {/* Detail drawer */}
      {selected && (
        <ProcessDrawer
          process={selectedWithMeta}
          runDate={todayDate}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
